import { Button } from '@/components/ui/button';
import { Zap, Lock, Globe } from 'lucide-react';
import { analytics } from '@/lib/analytics';

interface HeroSectionProps {
  onOpenAuth?: (mode: 'login' | 'register') => void;
}

export default function HeroSection({ onOpenAuth }: HeroSectionProps) {
  const scrollToFeatures = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
    analytics.event('scroll_to_section', { section: 'how_it_works', source: 'hero_cta' });
  };

  const handleStartFreeClick = () => {
    analytics.ctaClick('start_free', 'hero');
    onOpenAuth?.('login');
  };

  return (
    <section className="relative bg-gradient-to-b from-blue-50 to-white pt-20 pb-16 md:pt-32 md:pb-24 overflow-hidden">
      {/* Floating shapes background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-100 rounded-full opacity-20 blur-3xl" />
        <div className="absolute top-40 right-20 w-48 h-48 bg-blue-200 rounded-full opacity-20 blur-3xl" />
        <div className="absolute bottom-20 left-1/3 w-40 h-40 bg-blue-100 rounded-full opacity-20 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div className="text-center lg:text-left">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Turn Web Forms into Telegram Messages
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0">
              Get instant notifications in Telegram when anyone fills out your form. No delays, no spam
              filters, no missed leads. Setup in 60 seconds.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <Button size="lg" className="text-lg px-8" onClick={handleStartFreeClick}>
                Start Free - 10 Leads/Month
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8" onClick={scrollToFeatures}>
                See How It Works ↓
              </Button>
            </div>

            <p className="text-sm text-gray-500">
              No credit card • Free forever • 2-minute setup
            </p>

            {/* Trust Signals */}
            <div className="flex flex-wrap gap-6 justify-center lg:justify-start mt-8 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-blue-600" />
                <span>&lt;1s delivery</span>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-blue-600" />
                <span>Secure</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-blue-600" />
                <span>99.9% uptime</span>
              </div>
            </div>
          </div>

          {/* Visual Placeholder */}
          <div className="flex items-center justify-center">
            <div className="w-full max-w-lg aspect-[4/3] bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg shadow-2xl flex items-center justify-center">
              <div className="text-center p-8">
                <p className="text-blue-900 font-semibold text-lg mb-2">Hero Animation</p>
                <p className="text-blue-700 text-sm">Form → Telegram</p>
                <p className="text-blue-600 text-xs mt-2">(Placeholder)</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
